import { useMemo, useState } from 'react';
import { CardImageSlider } from '@/components';
import { useHotelsData } from '@/hooks';
import { toPathSegment } from '@/utils/imageUrl';
import { Card, CardBody, Col, Container, Row, Spinner, Button } from 'react-bootstrap';
import Link from 'next/link';
import { BsArrowUpRightCircleFill, BsGeoAlt, BsGeoAltFill, BsStarFill } from 'react-icons/bs';
import { FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa';
import { FaBed } from 'react-icons/fa6';
import { FaUtensils } from 'react-icons/fa';
import { HOTEL_CATALOG_NOTE } from '@/shared/hotelPolicyCopy';

const formatBoardType = (board) => {
  if (!board) return null;
  const s = String(board).toLowerCase();
  if (s.includes('all') && s.includes('inclusive')) return 'All Inclusive';
  if (s.includes('half') && s.includes('board')) return 'Half Board';
  if (s.includes('full') && s.includes('board')) return 'Full Board';
  if (s.includes('bed') && s.includes('breakfast')) return 'Bed & Breakfast';
  return String(board)
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
};

const getBoardType = (hotel) => {
  if (Array.isArray(hotel?.boards) && hotel.boards.length > 0) {
    const first = hotel.boards[0];
    return typeof first === 'string' ? first : first?.name ?? first?.value ?? null;
  }
  if (hotel?.board_type) return hotel.board_type;
  return (
    hotel?.raw?.board_type ||
    hotel?.raw?.agreement?.board_type ||
    hotel?.raw?.agreements?.[0]?.board_type ||
    hotel?.raw?.rooms?.[0]?.board_type ||
    hotel?.raw?.boards?.[0] ||
    null
  );
};

const HotelGridCard = ({ hotel }) => {
  const carouselImages = useMemo(() => {
    const list = Array.isArray(hotel.images) ? hotel.images.filter(Boolean) : [];
    return list;
  }, [hotel.images]);

  const hasImages = carouselImages.length > 0;
  const regionSlug = toPathSegment(hotel.region?.slug, '');
  const regionLabel = hotel.region?.name || hotel.destination?.name || hotel.location || null;
  const regionHref = regionSlug ? `/east-crete/${encodeURIComponent(regionSlug)}` : '/east-crete';
  const boardTypeLabel = formatBoardType(getBoardType(hotel));
  const starsValue = Number(hotel?.stars ?? hotel?.raw?.stars);
  const hotelStars = Number.isInteger(starsValue) && starsValue >= 2 && starsValue <= 7 ? starsValue : null;

  return (
    <Card className="hotels-service-card h-100 border-0">
      <div className="hotels-service-card__media position-relative">
        {regionLabel ? (
          <div className="hotels-service-card__overlay-top">
            <Link
              href={regionHref}
              className="featured-hotels-card__region featured-hotels-card__region--top featured-hotels-card__region--interactive"
              aria-label={`${regionLabel} — view region`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BsGeoAltFill className="featured-hotels-card__region-pin" aria-hidden />
              <span className="featured-hotels-card__region-name">{regionLabel}</span>
              <BsArrowUpRightCircleFill className="featured-hotels-card__region-arrow" aria-hidden />
            </Link>
          </div>
        ) : null}

        {hasImages ? (
          <CardImageSlider
            images={carouselImages}
            alt={hotel.name}
            className="hotels-grid-card-slider card-image-slider rounded-2 overflow-hidden"
            imgStyle={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block'
            }}
          />
        ) : (
          <div className="hotel-image-placeholder rounded-2 overflow-hidden">
            <FaBed className="hotel-image-placeholder-icon" />
          </div>
        )}

        {boardTypeLabel ? (
          <div className="hotels-service-card__overlay-bottom">
            <span className="hotels-service-card__overlay-chip hotels-service-card__overlay-chip--board">
              <FaUtensils size={13} aria-hidden />
              <span>{boardTypeLabel}</span>
            </span>
          </div>
        ) : null}
      </div>

      <CardBody className="hotels-service-card__content">
        <div className="hotels-service-card__title-row">
          <h5 className="hotels-service-card__title mb-0">
            {hotel.name}
          </h5>
          {hotelStars ? (
            <span className="hotels-service-card__stars-list" aria-hidden>
              {Array.from({ length: hotelStars }).map((_, idx) => (
                <BsStarFill key={idx} className="hotels-service-card__star-icon" />
              ))}
            </span>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
};

const ShowHotels = ({
  applyTopOffset = false,
  usePrimaryHeading = false,
  title = 'Selected Hotels in East Crete',
  introText = 'Discover handpicked hotels across East Crete, carefully selected to match different travel styles, preferences, and expectations for every type of guest.',
}) => {
  const { hotels, loading, error } = useHotelsData(false);
  const HeadingTag = usePrimaryHeading ? "h1" : "h2";
  const [activeRegion, setActiveRegion] = useState('all');
  const [activeStars, setActiveStars] = useState('all');

  const regionOptions = useMemo(() => {
    const map = new Map();
    hotels.forEach((hotel) => {
      const regionId = hotel?.region?.id != null ? String(hotel.region.id) : null;
      const regionName = hotel?.region?.name?.trim();
      if (!regionId || !regionName) return;
      if (!map.has(regionId)) {
        map.set(regionId, regionName);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [hotels]);

  const starsOptions = useMemo(() => {
    const set = new Set();
    hotels.forEach((hotel) => {
      if (Number.isInteger(hotel?.stars) && hotel.stars > 0) {
        set.add(hotel.stars);
      }
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [hotels]);

  const filteredHotels = useMemo(() => {
    return hotels.filter((hotel) => {
      const hotelRegionId = hotel?.region?.id != null ? String(hotel.region.id) : null;
      const matchesRegion = activeRegion === 'all' || hotelRegionId === activeRegion;
      const matchesStars = activeStars === 'all' || Number(hotel?.stars) === Number(activeStars);
      return matchesRegion && matchesStars;
    });
  }, [hotels, activeRegion, activeStars]);

  if (loading && hotels.length === 0) {
    return (
      <section className="py-5">
        <Container className="text-center">
                <Spinner animation="border" variant="primary" />
        </Container>
      </section>
    );
  }

  if (error && hotels.length === 0) {
    return (
      <section className="py-5">
        <Container>
          <div className="alert alert-warning text-center">
                <h5 className="alert-heading">Hotels Not Available</h5>
                <p className="mb-2">{error}</p>
                <p className="mb-0 small">
              <strong>For Developers:</strong> The backend endpoint <code>GET /backend/api/accommodations/with-agreements</code> needs to be available.
                </p>
              </div>
        </Container>
      </section>
    );
  }

  return (
    <section className={applyTopOffset ? "pt-4 pt-lg-5" : "pt-0"}>
      <Container>
        <Row className="mb-4">
          <Col>
            <HeadingTag className="sec-title mb-3">{title}</HeadingTag>
            <p className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: introText }} />
            <p className="small text-muted mb-0">{HOTEL_CATALOG_NOTE}</p>
          </Col>
        </Row>

        {hotels.length > 0 ? (
          <Row className="mb-4">
            <Col>
              <div className="service-filter-row-card hotels-filter-row">
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="service-filter-group-label">
                      <BsGeoAlt size={13} aria-hidden />
                      Region
                    </span>
                    <Button
                      type="button"
                      variant="light"
                      className={`service-filter-chip ${activeRegion === 'all' ? 'is-active' : ''}`}
                      onClick={() => setActiveRegion('all')}
                    >
                      All
                    </Button>
                    {regionOptions.map((region) => (
                      <Button
                        key={region.id}
                        type="button"
                        variant="light"
                        className={`service-filter-chip ${activeRegion === region.id ? 'is-active' : ''}`}
                        onClick={() => setActiveRegion(region.id)}
                      >
                        {region.name}
                      </Button>
                    ))}
                  </div>

                  <div className="service-filter-divider d-none d-lg-block" aria-hidden />

                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="service-filter-group-label">
                      <BsStarFill size={12} aria-hidden />
                      Stars
                    </span>
                    <Button
                      type="button"
                      variant="light"
                      className={`service-filter-chip ${activeStars === 'all' ? 'is-active' : ''}`}
                      onClick={() => setActiveStars('all')}
                    >
                      All
                    </Button>
                    {starsOptions.map((stars) => (
                      <Button
                        key={stars}
                        type="button"
                        variant="light"
                        className={`service-filter-chip d-flex align-items-center gap-1 ${activeStars === String(stars) ? 'is-active' : ''}`}
                        onClick={() => setActiveStars(String(stars))}
                      >
                        <BsStarFill size={12} />
                        {stars}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        ) : null}

        {filteredHotels.length > 0 ? (
          <Row className="g-4">
            {filteredHotels.map((hotel, idx) => (
                <Col key={hotel.id || idx} md={6} xl={4}>
                  <HotelGridCard hotel={hotel} />
              </Col>
            ))}
          </Row>
        ) : (
          <Row>
            <Col className="text-center">
              <p className="text-muted mb-0">
                {hotels.length === 0
                  ? 'No hotels available at the moment.'
                  : 'No hotels match these filters yet.'}
              </p>
            </Col>
          </Row>
        )}

        <Row className="mt-5">
          <Col xs={12}>
            <div className="text-center py-4">
              <p className="text-muted mb-2">
                All hotels are located in beautiful East Crete
              </p>
              <Button as={Link} href="/east-crete" variant="link" className="text-primary">
                <FaMapMarkerAlt className="me-2" />
                Discover East Crete Destinations
                <FaArrowRight className="ms-2" size={12} />
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default ShowHotels;
